import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const STATUSES = ["Started", "In-Progress", "Partial", "Completed", "AMC"];
const EMPTY_BANK = {
  bank_name: "",
  account_number: "",
  ifsc: "",
  account_holder: "",
};

export default function VendorFormDialog({ open, onClose, onSubmit, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: "",
    purpose: "",
    status: "Started",
    bank_quotation_value: "",
    actual_value: "",
    total_payments: "",
    bank_account: { ...EMPTY_BANK },
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {

      // eslint-disable-next-line
      setForm({
        name: initial.name || "",
        purpose: initial.purpose || "",
        status: initial.status || "Started",
        bank_quotation_value:
          initial.bank_quotation_value != null
            ? String(initial.bank_quotation_value)
            : "",
        actual_value:
          initial.actual_value != null ? String(initial.actual_value) : "",
        total_payments:
          initial.total_payments != null ? String(initial.total_payments) : "",
        bank_account: { ...EMPTY_BANK, ...(initial.bank_account || {}) },
      });
    } else {
      setForm({
        name: "",
        purpose: "",
        status: "Started",
        bank_quotation_value: "",
        actual_value: "",
        total_payments: "",
        bank_account: { ...EMPTY_BANK },
      });
    }
  }, [initial, open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateBank = (k, v) =>
    setForm((f) => ({ ...f, bank_account: { ...f.bank_account, [k]: v } }));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Vendor name is required");
      return;
    }
    const num = (v) => (v === "" ? 0 : parseFloat(v) || 0);
    const isEmptyBank = (b) =>
      !b?.bank_name && !b?.account_number && !b?.ifsc && !b?.account_holder;
    const payload = {
      name: form.name,
      purpose: form.purpose,
      status: form.status,
      bank_quotation_value: num(form.bank_quotation_value),
      actual_value: num(form.actual_value),
      total_payments: num(form.total_payments),
      bank_account: isEmptyBank(form.bank_account) ? null : form.bank_account,
    };
    setSaving(true);
    try {
      await onSubmit(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="vendor-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {isEdit ? "Edit Vendor" : "New Vendor"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Capture vendor commercial, banking and engagement details.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Vendor Name
            </Label>
            <Input
              data-testid="vendor-form-name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Company / agency name"
              className="mt-1.5"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Purpose / Scope
            </Label>
            <Textarea
              data-testid="vendor-form-purpose"
              value={form.purpose}
              onChange={(e) => update("purpose", e.target.value)}
              rows={2}
              placeholder="e.g. Office HVAC AMC, Hardware procurement"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Status
            </Label>
            <Select value={form.status} onValueChange={(v) => update("status", v)}>
              <SelectTrigger className="mt-1.5" data-testid="vendor-form-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Bank Quotation Value (₹)
            </Label>
            <Input
              data-testid="vendor-form-bank-value"
              type="number"
              min="0"
              value={form.bank_quotation_value}
              onChange={(e) => update("bank_quotation_value", e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Actual Value of Work (₹)
            </Label>
            <Input
              data-testid="vendor-form-actual-value"
              type="number"
              min="0"
              value={form.actual_value}
              onChange={(e) => update("actual_value", e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Total Payments Made (₹)
            </Label>
            <Input
              data-testid="vendor-form-total-payments"
              type="number"
              min="0"
              value={form.total_payments}
              onChange={(e) => update("total_payments", e.target.value)}
              className="mt-1.5"
            />
          </div>

          <div className="col-span-2 mt-3 pt-4 border-t border-slate-200">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Bank Account
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Optional — used for payments reconciliation.
            </p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">
              Bank Name
            </Label>
            <Input
              data-testid="vendor-bank-name"
              value={form.bank_account.bank_name}
              onChange={(e) => updateBank("bank_name", e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">
              Account Holder
            </Label>
            <Input
              data-testid="vendor-bank-holder"
              value={form.bank_account.account_holder}
              onChange={(e) => updateBank("account_holder", e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">
              Account Number
            </Label>
            <Input
              data-testid="vendor-bank-account"
              value={form.bank_account.account_number}
              onChange={(e) => updateBank("account_number", e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">
              IFSC
            </Label>
            <Input
              data-testid="vendor-bank-ifsc"
              value={form.bank_account.ifsc}
              onChange={(e) => updateBank("ifsc", e.target.value.toUpperCase())}
              className="mt-1.5"
            />
          </div>

          <div className="col-span-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
            💡 PoCs and attachments are managed from the vendor detail page after creation.
          </div>
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
            data-testid="vendor-form-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
            data-testid="vendor-form-save"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create vendor"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
